import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { GrnRegisterRow, PutawayTatRow, QcAnalysisRow } from '@/types/reports';
import { withId } from './reportHelpers';

const TABS = ['grnRegister', 'qcAnalysis', 'putawayTat'] as const;

export default function InboundQualityReportsPage() {
  const { t } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const { data: processConfiguration } = useProcessConfiguration();
  const showClientColumn = Boolean(processConfiguration?.thirdPartyLogisticsEnabled);
  const [tab, setTab] = useState(0);

  const { data: grns = [], isLoading: loadingGrn } = useQuery({
    queryKey: ['reports', 'grn-register'],
    queryFn: async () => (await apiClient.get<GrnRegisterRow[]>('/reports/grn-register')).data,
    enabled: tab === 0,
  });
  const { data: qcTasks = [], isLoading: loadingQc } = useQuery({
    queryKey: ['reports', 'qc-analysis'],
    queryFn: async () => (await apiClient.get<QcAnalysisRow[]>('/reports/qc-analysis')).data,
    enabled: tab === 1,
  });
  const { data: putawayTasks = [], isLoading: loadingPutaway } = useQuery({
    queryKey: ['reports', 'putaway-tat'],
    queryFn: async () => (await apiClient.get<PutawayTatRow[]>('/reports/putaway-tat')).data,
    enabled: tab === 2,
  });

  const grnColumns: DataTableColumn<ReturnType<typeof withId<GrnRegisterRow>>[number]>[] = [
    { field: 'grnNumber', headerName: t('columns.number') },
    { field: 'poNumber', headerName: t('columns.poNumber'), render: (r) => r.poNumber ?? '—' },
    { field: 'supplierName', headerName: t('columns.supplier'), filterable: true },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'grnDate', headerName: t('columns.date'), render: (r) => formatDate(r.grnDate) },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'receivedQty', headerName: t('columns.receivedQty') },
    { field: 'acceptedQty', headerName: t('columns.acceptedQty') },
    { field: 'rejectedQty', headerName: t('columns.rejectedQty') },
    { field: 'batchNo', headerName: t('columns.batch'), render: (r) => r.batchNo ?? '—' },
    { field: 'qcStatus', headerName: t('columns.qcStatus'), render: (r) => (r.qcStatus ? <StatusChip status={r.qcStatus} /> : '—'), filterable: true },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<GrnRegisterRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<GrnRegisterRow>>[number]>]
      : []),
  ];

  const qcColumns: DataTableColumn<ReturnType<typeof withId<QcAnalysisRow>>[number]>[] = [
    { field: 'grnNumber', headerName: t('columns.grnNumber'), filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'sampleSize', headerName: t('columns.sampleSize'), render: (r) => r.sampleSize ?? '—' },
    { field: 'aqlLevel', headerName: t('columns.aqlLevel'), render: (r) => r.aqlLevel ?? '—' },
    { field: 'inspectorUserName', headerName: t('columns.inspector'), render: (r) => r.inspectorUserName ?? '—' },
    { field: 'acceptedQty', headerName: t('columns.acceptedQty') },
    { field: 'rejectedQty', headerName: t('columns.rejectedQty') },
  ];

  const putawayColumns: DataTableColumn<ReturnType<typeof withId<PutawayTatRow>>[number]>[] = [
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'qty', headerName: t('columns.quantity') },
    { field: 'suggestedLocationCode', headerName: t('columns.suggestedLocation'), render: (r) => r.suggestedLocationCode ?? '—' },
    { field: 'confirmedLocationCode', headerName: t('columns.confirmedLocation'), render: (r) => r.confirmedLocationCode ?? '—' },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'createdDate', headerName: t('columns.raisedDate'), render: (r) => formatDate(r.createdDate) },
    { field: 'tatHours', headerName: t('columns.tatHours'), render: (r) => (r.tatHours != null ? r.tatHours.toFixed(1) : '—') },
  ];

  return (
    <>
      <PageHeader title={t('modules.inboundQuality.title')} subtitle={t('modules.inboundQuality.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((key) => (
          <Tab key={key} label={t(`${key}.title`)} />
        ))}
      </Tabs>

      <Box hidden={tab !== 0}>
        <DataTable columns={grnColumns} rows={withId(grns, (r) => `${r.grnId}-${r.materialCode}`)} loading={loadingGrn} searchable exportFileName="grn-register" />
      </Box>
      <Box hidden={tab !== 1}>
        <DataTable columns={qcColumns} rows={withId(qcTasks, (r) => r.qcTaskId)} loading={loadingQc} searchable exportFileName="qc-analysis" />
      </Box>
      <Box hidden={tab !== 2}>
        <DataTable columns={putawayColumns} rows={withId(putawayTasks, (r) => r.putawayTaskId)} loading={loadingPutaway} searchable exportFileName="putaway-tat" />
      </Box>
    </>
  );
}
